import { useEffect, useState } from 'react';

// Whole seconds between `startedAt` and now, never negative; 0 when nullish or
// unparseable. Module-level (a stable reference) so the effect below can call it
// without listing it as a dependency.
const computeElapsed = (startedAt) => {
  if (startedAt == null) return 0;
  const start = new Date(startedAt).getTime();
  if (Number.isNaN(start)) return 0;
  return Math.max(0, Math.floor((Date.now() - start) / 1000));
};

/**
 * Live-ticking elapsed seconds since `startedAt`, client-side only.
 *
 * Drives the running-timer hero's second-by-second count WITHOUT polling the
 * server — a local setInterval; the timer's start time is authoritative and the
 * display is derived from the wall clock. Returns 0 when `startedAt` is nullish
 * (no timer running).
 *
 * @param {string|number|Date|null} startedAt ISO string / epoch / Date, or null.
 * @param {number} [intervalMs=1000] Tick cadence.
 * @returns {number} Whole seconds elapsed (never negative).
 */
export function useElapsed(startedAt, intervalMs = 1000) {
  const [seconds, setSeconds] = useState(() => computeElapsed(startedAt));

  useEffect(() => {
    if (startedAt == null) {
      setSeconds(0);
      return undefined;
    }
    // Recompute immediately so a start-time change reflects without waiting a
    // full interval, then tick.
    setSeconds(computeElapsed(startedAt));
    const id = setInterval(() => setSeconds(computeElapsed(startedAt)), intervalMs);
    return () => clearInterval(id);
  }, [startedAt, intervalMs]);

  return seconds;
}

export default useElapsed;
